import { useState } from "react";
import { CloseIcon } from "../../../components/Icons";
import { Button } from "../../../components/ui/button";
import { useTemplatePresetMutations, useTemplatePresets } from "../../../hooks";

interface ApplyTemplatePresetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  termId: string | null;
  onApplied: () => void;
}

export function ApplyTemplatePresetDialog({
  open,
  onOpenChange,
  termId,
  onApplied,
}: ApplyTemplatePresetDialogProps) {
  if (!open) return null;

  return (
    <ApplyTemplatePresetDialogContent
      onOpenChange={onOpenChange}
      termId={termId}
      onApplied={onApplied}
    />
  );
}

// ダイアログを開くまでプリセット一覧を取得しないよう、中身を別コンポーネントに分ける
function ApplyTemplatePresetDialogContent({
  onOpenChange,
  termId,
  onApplied,
}: Omit<ApplyTemplatePresetDialogProps, "open">) {
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const { templatePresets, loading: presetsLoading } = useTemplatePresets();
  const { applyTemplatePresetToTerm, loading, error } =
    useTemplatePresetMutations();

  const closeDialog = () => {
    // 取り込み中に閉じると完了を拾えず、置き換わった設問が画面に反映されないまま終わる
    if (loading) return;
    setSelectedPresetId(null);
    onOpenChange(false);
  };

  const handleApply = async () => {
    if (!termId || !selectedPresetId) return;
    const applied = await applyTemplatePresetToTerm(selectedPresetId, termId);
    if (!applied) return;
    closeDialog();
    onApplied();
    alert("テンプレートスクリプトを取り込みました");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={closeDialog}
    >
      <div
        className="max-h-[90vh] w-[90%] max-w-[640px] overflow-auto rounded-xl bg-white p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-6 flex items-center justify-between">
          <h3 className="font-bold text-lg">テンプレートから取り込む</h3>
          <button
            type="button"
            onClick={closeDialog}
            disabled={loading}
            className="cursor-pointer border-none bg-none p-1 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-4 rounded-md bg-[var(--color-bg-subtle-gray)] p-4 text-sm">
          <ul className="list-disc pl-5 text-[var(--color-text-secondary)]">
            <li>
              このタームの既存のテンプレート項目はすべて削除され、選んだテンプレートの内容に置き換わります
            </li>
            <li>
              取り込み後もこのターム内で自由に編集できます。テンプレート側には影響しません
            </li>
            <li>
              すでにインタビュースクリプトが作成されたインタビューには反映されません（記入済みの内容を保持するため）
            </li>
          </ul>
        </div>

        {presetsLoading ? (
          <p className="py-6 text-center text-[var(--color-text-secondary)] text-sm">
            読み込み中...
          </p>
        ) : templatePresets.length === 0 ? (
          <p className="py-6 text-center text-[var(--color-text-secondary)] text-sm">
            テンプレートスクリプトがまだ登録されていません
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {templatePresets.map((preset) => (
              <label
                key={preset.templatePresetId}
                className="flex cursor-pointer items-start gap-3 rounded-md border border-[var(--color-border-default)] p-3"
              >
                <input
                  type="radio"
                  name="templatePreset"
                  value={preset.templatePresetId}
                  checked={selectedPresetId === preset.templatePresetId}
                  onChange={() => setSelectedPresetId(preset.templatePresetId)}
                  className="mt-1"
                />
                <span>
                  <span className="block font-medium text-sm">
                    {preset.name}
                  </span>
                  <span className="block text-[var(--color-text-secondary)] text-xs">
                    {preset.itemCount}件
                    {preset.description ? ` / ${preset.description}` : ""}
                  </span>
                </span>
              </label>
            ))}
          </div>
        )}

        {error && (
          <p className="mt-4 text-[var(--color-text-danger)] text-sm">
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={closeDialog}
            disabled={loading}
          >
            キャンセル
          </Button>
          <Button
            type="button"
            onClick={handleApply}
            disabled={loading || !selectedPresetId}
          >
            {loading ? "取り込み中..." : "取り込む"}
          </Button>
        </div>
      </div>
    </div>
  );
}
